import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PhoneCall, Mail, Clock, Hourglass, Target, Trophy, BarChart3, Mic, Phone } from "lucide-react";

export default function OwnerReports() {
  const { lang } = useI18n();
  const L = lang === "el";
  const [reps, setReps] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [recordingsCount, setRecordingsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => {
      const onlyReps = (r.data || []).filter((u) => u.role === "sales_rep");
      setReps(onlyReps);
      if (onlyReps.length && !selectedId) setSelectedId(onlyReps[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) { setData(null); setRecordingsCount(0); return; }
    setLoading(true);
    Promise.all([
      api.get(`/dashboard/rep-kpis/${selectedId}`),
      api.get(`/rep/${selectedId}/recordings-count`).catch(() => ({ data: { count: 0 } })),
    ])
      .then(([r, rec]) => { setData(r.data); setRecordingsCount(rec.data?.count || 0); })
      .finally(() => setLoading(false));
  }, [selectedId]);

  const Kpi = ({ icon: Icon, label, value, sub, accent = "#0052CC", testid }) => (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
          <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="owner-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Αναφορές" : "Reports"}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {L ? "Απόδοση πωλητών" : "Sales rep performance"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {L ? "Επιλέξτε έναν πωλητή για να δείτε αναλυτικά στοιχεία" : "Pick a sales rep to see detailed metrics"}
          </p>
        </div>
        <div className="min-w-[260px]">
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Πωλητής" : "Sales rep"}</label>
          <select
            data-testid="owner-reports-rep-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm bg-white"
          >
            <option value="">{L ? "— Επιλέξτε —" : "— Select —"}</option>
            {reps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      </div>

      {!selectedId && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
          {reps.length === 0
            ? (L ? "Δεν έχετε ακόμη πωλητές. Προσθέστε από την ενότητα 'Πωλητές'." : "No sales reps yet. Add them from the 'Sales Reps' section.")
            : (L ? "Επιλέξτε έναν πωλητή για να δείτε τα στοιχεία του." : "Pick a sales rep to view their metrics.")}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">{L ? "Φόρτωση..." : "Loading..."}</div>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi
              testid="kpi-calls"
              icon={PhoneCall}
              label={L ? "Κλήσεις" : "Calls"}
              value={data.calls}
              sub={`${data.completed_calls} ${L ? "ολοκληρωμένες" : "completed"} · ${data.call_minutes} ${L ? "λεπτά" : "min"}`}
              accent="#0052CC"
            />
            <Kpi
              testid="kpi-recordings"
              icon={Mic}
              label={L ? "Ηχογραφήσεις" : "Recordings"}
              value={recordingsCount}
              sub={L ? "Διαθέσιμες προς ακρόαση" : "Available to listen"}
              accent="#16A34A"
            />
            <Kpi
              testid="kpi-remaining-hours"
              icon={Hourglass}
              label={L ? "Υπόλοιπες εργατώρες" : "Remaining hours"}
              value={`${data.remaining_hours}h`}
              sub={`${data.used_hours}h ${L ? "από" : "of"} ${data.allocated_hours}h · ${data.reported_entries || 0} ${L ? "καταχωρ." : "entries"}`}
              accent={data.remaining_hours > 0 ? "#16A34A" : "#EF4444"}
            />
            <Kpi
              testid="kpi-talk-minutes"
              icon={Phone}
              label={L ? "Λεπτά ομιλίας" : "Talk minutes"}
              value={`${data.talk_minutes_remaining || 0}`}
              sub={`${data.talk_minutes_used || 0} ${L ? "χρησ." : "used"} / ${data.talk_minutes_allocated || 0} ${L ? "συν." : "total"}`}
              accent={data.talk_minutes_remaining > 0 ? "#0EA5E9" : "#EF4444"}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {L ? "Επίδοση πωλήσεων" : "Sales performance"}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Stat icon={Target} label={L ? "Επαφές" : "Contacts"} value={data.contacts} />
                <Stat icon={BarChart3} label={L ? "Ευκαιρίες" : "Opportunities"} value={data.opportunities} />
                <Stat icon={Trophy} label={L ? "Κερδισμένες" : "Won"} value={data.deals_won} color="#16A34A" />
                <Stat label={L ? "Χαμένες" : "Lost"} value={data.deals_lost} color="#EF4444" />
                <Stat label={L ? "Συναντήσεις" : "Meetings"} value={data.meetings} />
                <Stat label={L ? "Μετατροπή" : "Conversion"} value={`${data.conversion_rate}%`} />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {L ? "Πρόσφατες κλήσεις" : "Recent calls"}
              </h2>
              {(data.recent_calls || []).length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">{L ? "Καμία κλήση ακόμη" : "No calls yet"}</div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {data.recent_calls.slice(0, 10).map((c) => (
                    <li key={c.id} className="py-2 text-sm flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-slate-900 truncate">{c.phone_number_called || (L ? "(χωρίς αριθμό)" : "(no number)")}</div>
                        <div className="text-xs text-slate-500">{new Date(c.date).toLocaleString(L ? "el-GR" : "en-GB")}</div>
                      </div>
                      <div className="text-xs text-slate-600 whitespace-nowrap pl-2">
                        {c.duration_minutes}m · {c.outcome.replace("_", " ")}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color = "#0052CC" }) {
  return (
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
        <div className="text-xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
